/* @flow */

import * as React from 'react';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import { Link } from 'react-router-dom';
import RequireForAccess from 'react-access';

import Loading from 'components/Loading/Loading';
import ElectroSelect from 'forms/components/inputs/ElectroSelectBasic/ElectroSelectBasic';

import WidgetActions from '../WidgetActions';
import InsufficientData from '../InsufficientData/InsufficientData';
import ChartLegend from '../ChartLegend/ChartLegend';
import type { CommonWidget } from '../../commonTypes';

import styles from './WidgetCard.module.scss';

export type WidgetInfo = { title: string, variant: string };

export type WidgetCardProps = {
  widgetInfos: WidgetInfo[],
  loading: boolean,
  thumbnail?: React.Node,
  isIE: boolean,
  children: React.Node,
  dataCount: number,
  permissions: Array<*>,
  legendKeys?: Object[],
  button?: {
    link: string | Object,
    label: string,
  },
  contentStyle?: Object,
  onVariantSelect?: (variant: string) => void,
  footer?: React.Node,
} & CommonWidget;

export default class WidgetCard extends React.PureComponent<WidgetCardProps> {
  renderTitle() {
    const {
      widgetInfos,
      widgetItem: { variant },
      size,
      onVariantSelect,
    } = this.props;
    const widgetInfo =
      (variant && widgetInfos.find(_widgetInfo => _widgetInfo.variant === variant)) ||
      widgetInfos[0];

    if (size === 'thumbnail' || widgetInfos.length < 2) {
      return { title: widgetInfo.title, disableTypography: false };
    }

    return {
      title: (
        <div className={styles.selectContainer}>
          <ElectroSelect
            field={{
              options: widgetInfos.map(_widgetInfo => ({
                name: _widgetInfo.title,
                id: _widgetInfo.variant,
              })),
              value: widgetInfo.variant,
              set(_field, value: string) {
                if (typeof onVariantSelect === 'function') {
                  onVariantSelect(value);
                }
              },
              bind() {},
            }}
          />
        </div>
      ),
      disableTypography: true,
    };
  }

  renderChildren() {
    const { children, loading, size, dataCount } = this.props;

    if (loading) {
      return <Loading />;
    }

    if (dataCount <= 0) {
      return <InsufficientData icon={size !== 'thumbnail'} />;
    }

    return children;
  }

  render() {
    const {
      isIE,
      size,
      widgetItem,
      isFullScreen,
      button,
      permissions,
      thumbnail,
      legendKeys,
      contentStyle,
      footer,
    } = this.props;

    const { title, disableTypography } = this.renderTitle();

    const buttonNode = button && (
      <Link className={styles['button-link']} to={button.link}>
        <Button color="primary" variant="contained">
          {button.label}
        </Button>
      </Link>
    );

    return (
      <Card className={styles[size]}>
        <CardHeader
          className={styles['card-header']}
          title={title}
          disableTypography={disableTypography}
          action={<WidgetActions widgetItem={widgetItem} size={size} isFullScreen={isFullScreen} />}
          classes={{
            ...(disableTypography ? { root: styles.cardHeaderRoot } : {}),
            action: styles.cardHeaderAction,
          }}
        />
        {(!isIE || size !== 'thumbnail') && (
          <React.Fragment>
            <Divider />
            {(size === 'thumbnail' && thumbnail) || (
              <CardContent className={`${styles['card-content']}`} style={contentStyle}>
                {this.renderChildren()}
                {footer}
              </CardContent>
            )}
          </React.Fragment>
        )}
        {size !== 'thumbnail' && (
          <div className={styles['button-container']}>
            {legendKeys && <ChartLegend position="left" keys={legendKeys} />}
            {buttonNode && (
              <RequireForAccess permissions={permissions}>{buttonNode}</RequireForAccess>
            )}
          </div>
        )}
      </Card>
    );
  }
}
